import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import {
  RUN_STATUSES,
  STEP_NAMES,
  STEP_STATUSES,
  type RunProgressNotification,
} from "@shared/types/runs";
import { DBT_JOBS_RUNS_PATH } from "@/hooks/use-dbt-jobs";
import { invalidateRunHistory } from "@/hooks/use-reliability";
import { DATA_LOCATION_PATH } from "@/hooks/use-data-location";
import { OVERVIEW_PATH, invalidateConnectionLists } from "@/hooks/use-overview";
import { RUNS_PATH, runPath } from "@/hooks/use-runs";
import { useAuth } from "@/lib/auth/AuthContext";
import { logger } from "@/lib/logger";
import { isStatusFrame, isTerminalRunStatus } from "@/lib/runs/runActivity";

/**
 * One live run feed per tab (Session 10, P12; ux-s10.md §2).
 *
 * The socket used to belong to the run page's hook, so every screen that wanted live progress
 * would have opened its own. Now there is one ref-counted feed per instance: the first strip
 * that needs it dials, every other strip shares that socket, and the last one to leave closes
 * it. A tab therefore opens one socket however many strips are on screen, and a screen with no
 * strip opens none.
 *
 * The provider also owns the one cache rule for live frames (SF3): a frame that moved a step or
 * the run (`STEP_*`, `RUN_RUNNING`, finalize's terminal frame) refreshes that run's detail at
 * once, and the runs list, the connections list and the overview at most once per 30 seconds per
 * tab, except a run's terminal frame, which refreshes them at once (#2005). A screen that lists
 * runs holds the feed through `useLiveRunRefresh` (`hooks/use-live-run-refresh.ts`): a frame
 * reaches this rule only while some component holds the feed. Per-table frames refresh nothing;
 * the strips fold them in place. A connection's detail (`GET /dataflows/:id`) is audited and is
 * never refreshed from here.
 *
 * Without the provider (a test that mounts one screen) a consumer gets a private feed with the
 * same socket behaviour and no cache rule, which is how the run page worked before.
 */

/** Must match `WS_PATH` in `server/services/WebSocketService.ts`. */
export const WS_PATH = "/ws/pipeline-status";
/** Must match `WS_SUBPROTOCOL` in `server/services/WebSocketService.ts`. */
export const WS_SUBPROTOCOL = "skydata.v1";
/** Must match `WS_BEARER_PREFIX` in `server/services/WebSocketService.ts`. */
export const WS_BEARER_PREFIX = "bearer.";

export const RECONNECT_BASE_MS = 1_000;
export const RECONNECT_MAX_MS = 30_000;

/** The list and overview refresh at most this often per tab, whatever the frame rate (SF3). */
export const LIST_INVALIDATION_INTERVAL_MS = 30_000;

/**
 * Where the live feed is. `reconnecting` is the one a page acts on: it is the signal to poll,
 * and it covers a closed socket, a refused upgrade and a token that is not ready yet.
 */
export type ProgressTransport = "disabled" | "connecting" | "live" | "reconnecting";

export interface FeedTransport {
  transport: ProgressTransport;
  reconnectAttempts: number;
}

export function backoffDelayMs(attempt: number): number {
  return Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
}

export function progressSocketUrl(instanceId: string): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}${WS_PATH}?instanceId=${encodeURIComponent(instanceId)}`;
}

const STEP_NAMES_SET: ReadonlySet<string> = new Set(STEP_NAMES);
const STEP_STATUS_SET: ReadonlySet<string> = new Set(STEP_STATUSES);
const RUN_STATUS_SET: ReadonlySet<string> = new Set(RUN_STATUSES);

function optionalIn(value: unknown, allowed: ReadonlySet<string>): boolean {
  return value === undefined || value === null || (typeof value === "string" && allowed.has(value));
}

/**
 * The shape the bridge broadcasts, or null. The three Session 10 fields are optional, and a
 * frame that carries one outside its closed set is dropped rather than guessed at.
 */
export function parseFeedFrame(raw: unknown): RunProgressNotification | null {
  if (typeof raw !== "object" || raw === null) return null;
  const message = raw as { type?: unknown; payload?: unknown };
  if (message.type !== "entity_progress" && message.type !== "status") return null;
  const payload = message.payload;
  if (typeof payload !== "object" || payload === null) return null;
  const row = payload as Record<string, unknown>;
  if (typeof row.runId !== "string" || typeof row.event !== "string") return null;
  if (!optionalIn(row.step, STEP_NAMES_SET)) return null;
  if (!optionalIn(row.stepStatus, STEP_STATUS_SET)) return null;
  if (!optionalIn(row.runStatus, RUN_STATUS_SET)) return null;
  return row as unknown as RunProgressNotification;
}

type FrameListener = (frame: RunProgressNotification) => void;

export interface RunFeedOptions {
  instanceId: string;
  /** A ref to the latest token getter: read when a socket dials, never during a render. */
  tokenSource: { readonly current: () => Promise<string | null> };
}

/**
 * The socket half, with no React in it: acquire, listen, release. The reconnect keeps the
 * donor's `min(1000 * 2^n, 30000)` backoff and resets on a successful open.
 */
export class RunFeed {
  private readonly frameListeners = new Set<FrameListener>();
  private readonly transportListeners = new Set<() => void>();
  private refs = 0;
  private socket: WebSocket | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;
  private generation = 0;
  private snapshot: FeedTransport = { transport: "disabled", reconnectAttempts: 0 };

  constructor(private readonly options: RunFeedOptions) {}

  get instanceId(): string {
    return this.options.instanceId;
  }

  /** How many consumers hold the socket open. For tests. */
  get holders(): number {
    return this.refs;
  }

  getSnapshot = (): FeedTransport => this.snapshot;

  subscribeTransport = (listener: () => void): (() => void) => {
    this.transportListeners.add(listener);
    return () => {
      this.transportListeners.delete(listener);
    };
  };

  onFrame(listener: FrameListener): () => void {
    this.frameListeners.add(listener);
    return () => {
      this.frameListeners.delete(listener);
    };
  }

  /** Holds the socket open until the returned release is called. Idempotent release. */
  acquire(): () => void {
    this.refs += 1;
    if (this.refs === 1) this.open();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.refs = Math.max(0, this.refs - 1);
      if (this.refs === 0) this.shutdown();
    };
  }

  private setTransport(transport: ProgressTransport, reconnectAttempts: number): void {
    if (
      this.snapshot.transport === transport &&
      this.snapshot.reconnectAttempts === reconnectAttempts
    ) {
      return;
    }
    this.snapshot = { transport, reconnectAttempts };
    for (const listener of [...this.transportListeners]) listener();
  }

  private open(): void {
    this.generation += 1;
    this.attempt = 0;
    this.setTransport("connecting", 0);
    void this.connect(this.generation);
  }

  private shutdown(): void {
    this.generation += 1;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      // Detach first: closing fires `onclose`, and a handler that still held the closure
      // would schedule a reconnect for a feed nobody is holding.
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.close();
    }
    this.attempt = 0;
    this.setTransport("disabled", 0);
  }

  private scheduleReconnect(generation: number): void {
    if (generation !== this.generation || this.refs === 0) return;
    const attempt = this.attempt;
    this.attempt = attempt + 1;
    this.setTransport("reconnecting", attempt + 1);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.connect(generation);
    }, backoffDelayMs(attempt));
  }

  private async connect(generation: number): Promise<void> {
    if (generation !== this.generation || this.refs === 0) return;
    let token: string | null = null;
    try {
      token = await this.options.tokenSource.current();
    } catch {
      // Left null: a token that cannot be minted is handled just below.
    }
    if (generation !== this.generation || this.refs === 0) return;
    // A token that is not ready yet is the same problem as a dropped socket.
    if (!token) {
      this.scheduleReconnect(generation);
      return;
    }

    let socket: WebSocket;
    try {
      socket = new WebSocket(progressSocketUrl(this.options.instanceId), [
        WS_SUBPROTOCOL,
        `${WS_BEARER_PREFIX}${token}`,
      ]);
    } catch {
      logger.warn("run progress socket could not be opened", {
        "skydata.surface": "live-feed",
      });
      this.scheduleReconnect(generation);
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      if (generation !== this.generation) return;
      this.attempt = 0;
      this.setTransport("live", 0);
    };
    socket.onmessage = (event: MessageEvent) => {
      if (generation !== this.generation) return;
      this.dispatch(event.data);
    };
    socket.onerror = () => {
      // `onclose` always follows, and that is where the retry lives.
      logger.warn("run progress socket reported an error", { "skydata.surface": "live-feed" });
    };
    socket.onclose = () => {
      if (generation !== this.generation) return;
      if (this.socket === socket) this.socket = null;
      this.scheduleReconnect(generation);
    };
  }

  private dispatch(data: unknown): void {
    let parsed: unknown;
    try {
      parsed = typeof data === "string" ? JSON.parse(data) : data;
    } catch {
      logger.warn("run progress frame was not JSON", { "skydata.surface": "live-feed" });
      return;
    }
    const frame = parseFeedFrame(parsed);
    if (!frame) return;
    for (const listener of [...this.frameListeners]) listener(frame);
  }
}

/* ------------------------------------------------------------------------------------- *
 * Imports started from this tab (SF13, S4)
 * ------------------------------------------------------------------------------------- */

/**
 * The runs this tab started, newest per connection. The list announces only the row whose
 * import was started here (SF13), and an open editor shows Queued from the start response
 * before the first read (S4). Module state on purpose: "this tab" is this JavaScript realm.
 */
class StartedRuns {
  private byDataflow = new Map<string, string>();
  private runs = new Set<string>();
  /**
   * Session 11 (P1): runs started several at a time by the list's "Import selected". They move
   * their rows like any start from this tab, but the list's one live line does not speak for
   * them: the page notice says what started (ux-s10 §3), and a line hopping between rows would not.
   */
  private quiet = new Set<string>();
  private version = 0;
  /** An immutable copy of `byDataflow`, replaced on every change (for `useStartedRuns`). */
  private snapshot: ReadonlyMap<string, string> = new Map();
  private readonly listeners = new Set<() => void>();

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getVersion = (): number => this.version;

  /** Connection id to the newest run this tab started for it; a new object on every change. */
  getSnapshot = (): ReadonlyMap<string, string> => this.snapshot;

  mark(runId: string, dataflowIds: readonly string[], options: { quiet?: boolean } = {}): void {
    this.runs.add(runId);
    if (options.quiet === true) this.quiet.add(runId);
    for (const id of dataflowIds) this.byDataflow.set(id, runId);
    this.snapshot = new Map(this.byDataflow);
    this.version += 1;
    for (const listener of [...this.listeners]) listener();
  }

  runFor(dataflowId: string): string | null {
    return this.byDataflow.get(dataflowId) ?? null;
  }

  startedHere(runId: string | null | undefined): boolean {
    return Boolean(runId && this.runs.has(runId));
  }

  /** True for a run marked `quiet` (Session 11, P1: one of an "Import selected" batch). */
  isQuiet(runId: string | null | undefined): boolean {
    return Boolean(runId && this.quiet.has(runId));
  }

  reset(): void {
    this.byDataflow = new Map();
    this.runs = new Set();
    this.quiet = new Set();
    this.snapshot = new Map();
    this.version += 1;
    for (const listener of [...this.listeners]) listener();
  }
}

export const startedRuns = new StartedRuns();

/**
 * The runs this tab started, by connection, as a value that changes identity when this tab
 * starts an import (so a memo can depend on it; review r1).
 */
export function useStartedRuns(): ReadonlyMap<string, string> {
  return useSyncExternalStore(
    startedRuns.subscribe,
    startedRuns.getSnapshot,
    startedRuns.getSnapshot,
  );
}

/** Re-renders when this tab starts an import. */
export function useStartedRunsVersion(): number {
  return useSyncExternalStore(
    startedRuns.subscribe,
    startedRuns.getVersion,
    startedRuns.getVersion,
  );
}

/* ------------------------------------------------------------------------------------- *
 * The cache rule
 * ------------------------------------------------------------------------------------- */

/**
 * Leading and trailing, at most one call per `intervalMs`: the first frame refreshes at once,
 * the frames inside the window collapse into one refresh at its end, so the last state of a
 * run always arrives.
 */
export function createThrottle(
  fn: () => void,
  intervalMs: number,
  now: () => number = () => Date.now(),
): { call: () => void; cancel: () => void } {
  let last = Number.NEGATIVE_INFINITY;
  let pending: ReturnType<typeof setTimeout> | null = null;
  const run = () => {
    last = now();
    pending = null;
    fn();
  };
  return {
    call: () => {
      const wait = last + intervalMs - now();
      if (wait <= 0 && !pending) {
        run();
        return;
      }
      if (!pending) pending = setTimeout(run, Math.max(0, wait));
    },
    cancel: () => {
      if (pending) clearTimeout(pending);
      pending = null;
    },
  };
}

/**
 * #2005: a run's own frames (`RUN_RUNNING` and the terminal frame) share ONE batch window: the
 * first refreshes the lists at once, the rest of the window collapse into one refresh at its end,
 * and never more than one refresh per window, however many runs start or finish (50 scheduled
 * imports in one minute cost at most 6 refreshes, not 100). So a finished run reads Succeeded on
 * every list within 10 s (`RUN_FRAME_BATCH_MS`), at once when no other run frame came in the 10 s
 * before it.
 */
export const RUN_FRAME_BATCH_MS = 10_000;

/** How a refresh reaches the cache: `none` only marks the entries stale (a hidden tab). */
export type RefetchType = "active" | "none";

/** True while the tab is hidden: a frame then only marks entries stale (#2005). */
export function tabHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

/** The batched refresh for a run's own frames; without it each frame refreshes at once. */
export interface RunFrameRefreshes {
  started: { call: () => void };
  finished: { call: () => void };
}

/** A run started: the lists, and the reliability read whose schedule outcome now reads Running. */
export function invalidateRunStarted(
  queryClient: QueryClient,
  refetchType: RefetchType = "active",
): void {
  invalidateLists(queryClient, refetchType);
  invalidateRunHistory(queryClient, refetchType);
}

/**
 * What a status frame refreshes (SF3): that run's detail at once, and the throttled lists.
 * Exactly these keys; never `[dataflowPath(id)]`, which is an audited read.
 *
 * #2005: the run's own two frames, `RUN_RUNNING` and finalize's terminal frame (the only frames
 * that carry `runStatus`), skip the 30 s throttle and go through the run-frame batch window
 * (`RUN_FRAME_BATCH_MS`), so every list reads Running and then Succeeded within 10 s. The step
 * frames between them stay throttled. In a hidden tab a frame only marks entries stale; the
 * provider refetches when the tab is shown again.
 */
export function invalidateForFrame(
  queryClient: QueryClient,
  frame: RunProgressNotification,
  lists: { call: () => void },
  runFrames: RunFrameRefreshes = {
    started: { call: () => invalidateRunStarted(queryClient) },
    finished: { call: () => invalidateRunFinished(queryClient) },
  },
): void {
  if (!isStatusFrame(frame)) return;
  void queryClient.invalidateQueries({
    queryKey: [runPath(frame.runId)],
    exact: true,
    refetchType: tabHidden() ? "none" : "active",
  });
  if (!frame.runStatus) {
    lists.call();
    return;
  }
  if (isTerminalRunStatus(frame.runStatus)) runFrames.finished.call();
  else runFrames.started.call();
}

/**
 * What a finished run refreshes (#2005), from its terminal frame (batched), from a screen that
 * read the finish itself (`useLiveRunRefresh`) and from the run page: every list rather than
 * through the 30 s throttle, plus the run history, Your data's location and the transform steps.
 * Never a step frame.
 */
export function invalidateRunFinished(
  queryClient: QueryClient,
  refetchType: RefetchType = "active",
): void {
  invalidateLists(queryClient, refetchType);
  // Last loaded and the rows trend come from the reliability queries, which stay fresh for a
  // minute, so refresh them with the status (#1653).
  invalidateRunHistory(queryClient, refetchType);
  // Your data's example table follows the newest landing, which a first load just changed.
  void queryClient.invalidateQueries({ queryKey: [DATA_LOCATION_PATH], refetchType });
  // The transform steps (the Transformations strip, the Overview's Tests card): a run's dbt step
  // ended with it.
  void queryClient.invalidateQueries({ queryKey: [DBT_JOBS_RUNS_PATH], refetchType });
}

export function invalidateLists(
  queryClient: QueryClient,
  refetchType: RefetchType = "active",
): void {
  void queryClient.invalidateQueries({ queryKey: [RUNS_PATH], refetchType });
  void queryClient.invalidateQueries({ queryKey: [OVERVIEW_PATH], refetchType });
  // #2094: the Connections list and the Overview's own copy of it, which its card counts.
  invalidateConnectionLists(queryClient, refetchType);
}

/**
 * The provider's refreshes (#2005): the step frames' 30 s list throttle and the run frames' one
 * batch window. In a hidden tab each only marks its entries stale and remembers it; showing the
 * tab again refetches what a finish touches, once.
 */
export function createFeedRefreshes(queryClient: QueryClient): {
  lists: { call: () => void; cancel: () => void };
  runFrames: RunFrameRefreshes;
  onVisible: () => void;
  cancel: () => void;
} {
  let staleWhileHidden = false;
  let finishInWindow = false;
  const lists = createThrottle(() => {
    if (tabHidden()) {
      staleWhileHidden = true;
      invalidateLists(queryClient, "none");
      return;
    }
    invalidateLists(queryClient);
  }, LIST_INVALIDATION_INTERVAL_MS);
  const batch = createThrottle(() => {
    const finished = finishInWindow;
    finishInWindow = false;
    const refetchType: RefetchType = tabHidden() ? "none" : "active";
    if (refetchType === "none") staleWhileHidden = true;
    if (finished) invalidateRunFinished(queryClient, refetchType);
    else invalidateRunStarted(queryClient, refetchType);
  }, RUN_FRAME_BATCH_MS);
  return {
    lists,
    runFrames: {
      started: { call: () => batch.call() },
      finished: {
        call: () => {
          finishInWindow = true;
          batch.call();
        },
      },
    },
    onVisible: () => {
      if (tabHidden()) return;
      // Frames that came while hidden only marked their entries stale; anything a frame marked
      // without a refetch is read now, once.
      if (staleWhileHidden) {
        staleWhileHidden = false;
        invalidateRunFinished(queryClient);
      }
      void queryClient.refetchQueries({ type: "active", stale: true, predicate: isRunKey });
    },
    cancel: () => {
      lists.cancel();
      batch.cancel();
    },
  };
}

/** A run detail entry (`[runPath(id)]`), which a hidden tab's frame marks stale. */
function isRunKey(query: { queryKey: readonly unknown[] }): boolean {
  const head = query.queryKey[0];
  return typeof head === "string" && head.startsWith(`${RUNS_PATH}/`);
}

/* ------------------------------------------------------------------------------------- *
 * React
 * ------------------------------------------------------------------------------------- */

interface FeedContextValue {
  feedFor(instanceId: string): RunFeed;
}

const FeedContext = createContext<FeedContextValue | null>(null);

export function InstanceRunFeedProvider({ children }: { children: ReactNode }) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  const tokenRef = useRef(getAccessToken);
  useEffect(() => {
    tokenRef.current = getAccessToken;
  }, [getAccessToken]);

  const [state] = useState(() => {
    const feeds = new Map<string, RunFeed>();
    const refreshes = createFeedRefreshes(queryClient);
    return { feeds, lists: refreshes.lists, runFrames: refreshes.runFrames, refreshes };
  });

  useEffect(() => {
    const onVisible = state.refreshes.onVisible;
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisible);
    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisible);
      }
      state.refreshes.cancel();
    };
  }, [state]);

  const value = useMemo<FeedContextValue>(
    () => ({
      feedFor(instanceId: string): RunFeed {
        let feed = state.feeds.get(instanceId);
        if (!feed) {
          feed = new RunFeed({ instanceId, tokenSource: tokenRef });
          feed.onFrame((frame) =>
            invalidateForFrame(queryClient, frame, state.lists, state.runFrames),
          );
          state.feeds.set(instanceId, feed);
        }
        return feed;
      },
    }),
    [state, queryClient],
  );

  return <FeedContext.Provider value={value}>{children}</FeedContext.Provider>;
}

/** True under `InstanceRunFeedProvider`, the only place a frame refreshes the cache (#2005). */
export function useSharedRunFeedAvailable(): boolean {
  return useContext(FeedContext) !== null;
}

/**
 * The feed for `instanceId`: the tab's shared one under the provider, or a private one (same
 * socket behaviour, no cache rule) without it. Null without an instance.
 */
export function useRunFeed(instanceId: string | null): RunFeed | null {
  const context = useContext(FeedContext);
  const { getAccessToken } = useAuth();
  const tokenRef = useRef(getAccessToken);
  useEffect(() => {
    tokenRef.current = getAccessToken;
  }, [getAccessToken]);
  const [privateFeeds] = useState(() => new Map<string, RunFeed>());
  if (!instanceId) return null;
  if (context) return context.feedFor(instanceId);
  let feed = privateFeeds.get(instanceId);
  if (!feed) {
    feed = new RunFeed({ instanceId, tokenSource: tokenRef });
    privateFeeds.set(instanceId, feed);
  }
  return feed;
}

const DISABLED: FeedTransport = { transport: "disabled", reconnectAttempts: 0 };
const noopSubscribe = () => () => {};
const disabledSnapshot = () => DISABLED;

/** The feed's transport, re-rendering on change. */
export function useFeedTransport(feed: RunFeed | null): FeedTransport {
  return useSyncExternalStore(
    feed ? feed.subscribeTransport : noopSubscribe,
    feed ? feed.getSnapshot : disabledSnapshot,
    feed ? feed.getSnapshot : disabledSnapshot,
  );
}
